import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/shared/lib/utils";

/** A single key/value row in a KeyValueGrid. */
export interface KeyValueItem {
	/** Stable key for React. */
	id: string;
	/** The label (term). */
	label: string;
	/** The value (definition) — string or rich node. */
	value: ReactNode;
	/** Optional lucide icon shown beside the label. */
	icon?: LucideIcon;
}

interface KeyValueGridProps {
	items: ReadonlyArray<KeyValueItem>;
	/** Column count at the `sm` breakpoint and up. Defaults to 2. */
	columns?: 1 | 2;
	className?: string;
}

/**
 * A designed key/value definition grid — the bespoke replacement for the source
 * docs' two-column "key | value" markdown tables. Each entry becomes a `<dt>/<dd>`
 * pair inside one glass card, with a hairline divider between rows; far calmer and
 * more legible than a bordered table for short label→value pairs.
 */
export function KeyValueGrid({ items, columns = 2, className }: KeyValueGridProps) {
	return (
		// One quiet surface with internal HAIRLINE dividers (border-t per row,
		// border-l on the right column) — not the old gap-px-over-gray-fill grid,
		// which read as a patchwork of gray boxes. The brand treatment: one flat
		// lavender-tinted definition surface (the `--accent` tint), warm hairline
		// rules between cells, deep-purple term labels, and NO shadow — matching the
		// resources-hub table family.
		<dl
			className={cn(
				"grid grid-cols-1 overflow-hidden rounded-2xl border border-border bg-accent/40",
				columns === 2 && "sm:grid-cols-2",
				className,
			)}
		>
			{items.map((item, index) => {
				const Icon = item.icon;
				return (
					<div
						key={item.id}
						className={cn(
							"flex flex-col p-5",
							// Hairline rules: every row after the first gets a top rule; in the
							// 2-col layout the right column gets a left rule and the first ROW
							// (two cells) drops its top rule.
							index > 0 && "border-t border-border",
							columns === 2 && index === 1 && "sm:border-t-0",
							columns === 2 && index % 2 === 1 && "sm:border-l sm:border-border",
						)}
					>
						<dt className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-accent-foreground">
							{Icon && <Icon className="h-3.5 w-3.5 text-primary" aria-hidden="true" />}
							{item.label}
						</dt>
						<dd className="mt-2 text-[15px] leading-relaxed text-foreground/90 [&_strong]:font-semibold [&_strong]:text-foreground">
							{item.value}
						</dd>
					</div>
				);
			})}
		</dl>
	);
}
